import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from '../../../../context/AuthContext'
import { useAccessContext } from '../../../../context/AccessContext'
import { useI18n } from '../../../../i18n/I18nContext'
import { useBrowserPlatform } from '../../../../hooks/useBrowserPlatform'
import { workerService } from '../../../../services/worker'
import { navigateTo } from '../../../../services/navigation'
import {
  WORKER_EXPERIENCE_WORKSPACES,
  visibleWorkspaceNavigationEntries,
  workspaceUrl,
  type NavigationAccessInput,
} from '../../admin/navigationRegistry'
import { installWorkerManifestLink } from '../../../../services/workerPwa'
import { WorkerMentorshipPanel } from '../WorkerMentorshipPanel'
import { MyWorkLogWorkspace } from '../../worklog/WorkLogWorkspace'
import { WorkerRequestRecovery, WorkerSectionUnavailable, workerSectionUnavailable } from '../WorkerRequestRecovery'
import type { WorkerWorkspaceState } from '../useWorkerWorkspaceData'
import { WorkerMobileAvailability } from './WorkerMobileAvailability'
import { WorkerMobileHome } from './WorkerMobileHome'
import { WorkerMobileLearning } from './WorkerMobileLearning'
import { WorkerMobileMessages } from './WorkerMobileMessages'
import { WorkerMobileDecisions, WorkerMobileMore } from './WorkerMobileMore'
import { WorkerMobilePermissions } from './WorkerMobilePermissions'
import { WorkerMobileProfile } from './WorkerMobileProfile'
import { WorkerMobileShifts } from './WorkerMobileShifts'
import { WorkerMobileTime } from './WorkerMobileTime'
import {
  MobileCard,
  MobileLoadingState,
} from './WorkerMobilePrimitives'
import {
  WORKER_MOBILE_TABS,
  activeTabFor,
  screenFromQuery,
  type WorkerMobileScreen,
  type WorkerMobileTab,
} from './workerMobileNavigation'

/**
 * Android-parity worker shell for phones and installed PWAs.
 *
 * This is a presentation of the SAME worker client as the wide layout, not a
 * separate product: identical canonical APIs, identical backend-provided
 * capability flags, identical tenant branding from `/api/access/my-context`.
 * The Android app remains the UX reference; anything a browser cannot do is
 * explained rather than faked.
 */

export const WorkerMobileShell: React.FC<{ state: WorkerWorkspaceState }> = ({ state }) => {
  const { t } = useI18n()
  const auth = useContext(AuthContext)
  const { accessContext, selectedTenantId, hasAnyPermission, currentBrandName, currentLogoUrl, brandStyleVars } = useAccessContext()
  const platform = useBrowserPlatform()
  const [screen, setScreen] = useState<WorkerMobileScreen>(() =>
    (typeof window === 'undefined' ? null : screenFromQuery(window.location.search)) || 'home')
  const [inviteCode, setInviteCode] = useState('')
  const [deepLinkThreadId, setDeepLinkThreadId] = useState<string | null>(null)

  const permissionKeys = useMemo(
    () => new Set(accessContext?.effectivePermissionKeys || []),
    [accessContext?.effectivePermissionKeys]
  )
  const workLogVisible = accessContext?.billingEntitlements?.features?.['worklog.project_time']?.enabled === true
    && permissionKeys.has('tenant.work_tasks.view')
    && permissionKeys.has('tenant.project_time.self_manage')
  const mentorshipVisible = accessContext?.uiCapabilities?.modules?.workerMentorship !== false

  /*
   * Other workspaces this user is authorized for, resolved from the same
   * backend-authoritative registry the desktop switcher uses. A worker-only
   * account resolves to an empty list, so More shows no switching at all.
   */
  const otherWorkspaces = useMemo(() => {
    const navigationContext: NavigationAccessInput = {
      accessContext,
      selectedTenantId,
      currentOrganization: null,
      hasAnyPermission,
    }
    // Learning and mentorship already have their own rows in More, so only
    // workspaces outside the worker experience are switch targets.
    return visibleWorkspaceNavigationEntries(navigationContext)
      .filter((entry) => !WORKER_EXPERIENCE_WORKSPACES.has(entry.key))
      .map((entry) => ({ key: entry.key, label: entry.resolveLabel({ t }) }))
  }, [accessContext, selectedTenantId, hasAnyPermission, t])
  const requiresOrgLink = Boolean(accessContext?.requiresOrgLink || state.data.punch?.requiresOrgLink)

  // The manifest is only linked on the workforce surface, so the consumer
  // Social host keeps its own separate installable identity.
  useEffect(() => installWorkerManifestLink(), [])

  const openScreen = useCallback((next: WorkerMobileScreen) => {
    setScreen(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Notification click routing. The Service Worker hands the page a URL rather
  // than routing itself, because only the page knows the worker's shell.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return undefined
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'punch-worker-notification-click') return
      const url = String(event.data.url || '')
      const query = url.includes('?') ? url.slice(url.indexOf('?')) : ''
      const threadId = new URLSearchParams(query).get('threadId')
      if (threadId) {
        setDeepLinkThreadId(threadId)
        openScreen('messages')
        return
      }
      openScreen(screenFromQuery(query) || 'home')
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [openScreen])

  useEffect(() => {
    if (screen === 'worklog' && !workLogVisible) setScreen('more')
  }, [screen, workLogVisible])

  const unreadMessages = state.data.threads.reduce((total, thread) => total + (thread.unreadCount || 0), 0)
  const pendingSyncCount = platform.queue.pending + platform.queue.syncing + platform.queue.failed
  const activeTab = activeTabFor(screen)

  if (state.loading) {
    return (
      <section className="wm-shell" style={brandStyleVars} data-testid="worker-mobile-shell">
        <MobileLoadingState />
      </section>
    )
  }

  const body = (() => {
    if (workerSectionUnavailable(state, screen)) return <WorkerSectionUnavailable retry={state.load} />
    switch (screen) {
      case 'home':
        return (
          <WorkerMobileHome
            state={state}
            platform={platform}
            onOpenPermissions={() => openScreen('permissions')}
            onOpenLearning={() => openScreen('learning')}
            onOpenTab={(tab) => openScreen(tab)}
          />
        )
      case 'shifts':
        return <WorkerMobileShifts state={state} />
      case 'messages':
        return (
          <WorkerMobileMessages
            state={state}
            initialThreadId={deepLinkThreadId}
            onConsumeInitialThread={() => setDeepLinkThreadId(null)}
          />
        )
      case 'time':
        return <WorkerMobileTime state={state} />
      case 'availability':
        return <WorkerMobileAvailability state={state} />
      case 'profile':
        return (
          <WorkerMobileProfile
            state={state}
            platform={platform}
            onOpenPermissions={() => openScreen('permissions')}
            onSignOut={() => auth?.logout()}
          />
        )
      case 'permissions':
        return <WorkerMobilePermissions platform={platform} />
      case 'decisions':
        return <WorkerMobileDecisions state={state} />
      case 'worklog':
        return <div className="wm-screen wm-screen--embedded"><MyWorkLogWorkspace /></div>
      case 'mentorship':
        return <div className="wm-screen wm-screen--embedded"><WorkerMentorshipPanel /></div>
      case 'learning':
        return <WorkerMobileLearning onClose={() => openScreen('home')} />
      default:
        return (
          <WorkerMobileMore
            workLogVisible={workLogVisible}
            mentorshipVisible={mentorshipVisible}
            pendingSyncCount={pendingSyncCount}
            otherWorkspaces={otherWorkspaces}
            onOpen={openScreen}
            onSwitchWorkspace={(workspace) => navigateTo(workspaceUrl(workspace as never))}
            onSignOut={() => auth?.logout()}
          />
        )
    }
  })()

  return (
    <section
      className="wm-shell"
      style={brandStyleVars}
      data-testid="worker-mobile-shell"
      data-standalone={platform.capabilities.standalone ? 'true' : undefined}
    >
      {screen !== 'learning' ? (
        <header className="wm-appbar">
          <div className="wm-appbar__brand">
            {currentLogoUrl ? <img src={currentLogoUrl} alt="" /> : null}
            <div>
              <small>{currentBrandName}</small>
              <h1>{t(WORKER_MOBILE_TABS.find((tab) => tab.id === activeTab)?.label || 'Today')}</h1>
            </div>
          </div>
          <div className="wm-appbar__actions">
            {!platform.online ? <span className="wm-offline-dot" role="status" aria-label={t('Offline')} /> : null}
            <button type="button" className="wm-icon-button" onClick={() => openScreen('learning')} aria-label={t('Open learning portal')}>◎</button>
            <button type="button" className="wm-icon-button" onClick={() => void state.load()} aria-label={t('Refresh')}>⟳</button>
          </div>
        </header>
      ) : null}

      <main className="wm-main" id="worker-mobile-content" tabIndex={-1}>
        {requiresOrgLink ? (
          <MobileCard tone="info" testId="worker-mobile-org-link">
            <strong>{t('Link an organization to unlock work operations.')}</strong>
            <p>{t('You can keep using Learning while your worker profile has no organization.')}</p>
            <form
              className="wm-form"
              onSubmit={(event) => {
                event.preventDefault()
                if (!inviteCode.trim()) return
                void state.run('org-link', () => workerService.linkOrganization(inviteCode.trim()), t('Organization linked.'))
              }}
            >
              <label className="wm-field">
                <span>{t('Invite code')}</span>
                <input value={inviteCode} onChange={(event) => setInviteCode(event.target.value)} autoComplete="off" />
              </label>
              <button
                type="submit"
                className="wm-button wm-button--primary"
                data-tone="info"
                disabled={Boolean(state.busy) || !inviteCode.trim()}
                data-testid="worker-mobile-link-org"
              >
                {t('Link organization')}
              </button>
            </form>
          </MobileCard>
        ) : null}

        <WorkerRequestRecovery key={`${auth?.user?.id}:${selectedTenantId || accessContext?.tenantId}`} state={state} />
        {state.notice ? <p className="wm-inline-notice" role="status">{state.notice}</p> : null}

        {body}
      </main>

      {screen !== 'learning' ? (
        <nav className="wm-bottom-nav" aria-label={t('Worker navigation')} data-testid="worker-mobile-bottom-nav">
          {WORKER_MOBILE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className="wm-nav-item"
              data-active={activeTab === tab.id || undefined}
              aria-current={activeTab === tab.id ? 'page' : undefined}
              onClick={() => openScreen(tab.id as WorkerMobileTab)}
              data-testid={`worker-mobile-nav-${tab.id}`}
            >
              <span className="wm-nav-item__icon" aria-hidden="true">{tab.icon}</span>
              <span className="wm-nav-item__label">{t(tab.label)}</span>
              {tab.id === 'messages' && unreadMessages ? (
                <span className="wm-nav-item__badge" aria-label={t('{count} unread messages', { count: String(unreadMessages) })}>
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              ) : null}
              {tab.id === 'more' && pendingSyncCount ? (
                <span className="wm-nav-item__badge" data-tone="warning" aria-label={t('{count} pending sync', { count: String(pendingSyncCount) })}>
                  {pendingSyncCount > 9 ? '9+' : pendingSyncCount}
                </span>
              ) : null}
            </button>
          ))}
        </nav>
      ) : null}
    </section>
  )
}
